import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Gift, Home, LogOut, MessageCircle, Package, ShoppingCart, Wallet } from "lucide-react";
import { OfferPopup } from "./OfferPopup";
import { ViewAsBar } from "../../components/common/ViewAsBar";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { CartProvider, useCart } from "../../context/CartContext";
import { api } from "../../lib/api";
import { RoleBadge } from "../../components/common/RoleBadge";
import { LangSwitch } from "../../components/common/LangSwitch";
import { Logo } from "../../components/common/Logo";
import { PullToRefresh } from "../../components/common/PullToRefresh";
import { NotificationBell } from "../../components/common/NotificationBell";
import { PushPrompt } from "../../components/common/PushPrompt";

const TABS = [
  { to: "/app", end: true, icon: Home, key: "home" },
  { to: "/app/products", icon: Package, key: "products" },
  { to: "/app/offers", icon: Gift, key: "offers" },
  { to: "/app/orders", icon: ClipboardList, key: "orders" },
  { to: "/app/account", icon: Wallet, key: "account" },
  { to: "/app/chat", icon: MessageCircle, key: "chat" },
];

const CartButton = () => {
  const { t } = useI18n();
  const { count } = useCart();
  return (
    <Link to="/app/cart" data-testid="header-cart-button" aria-label={t("shop.cart")} className="relative grid h-11 w-11 place-items-center rounded-full transition-colors hover:bg-white/10">
      <ShoppingCart className="h-5 w-5" />
      {count > 0 && (
        <span data-testid="header-cart-count" className="absolute right-0.5 top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-navy ring-2 ring-navy animate-in zoom-in duration-200">
          {count}
        </span>
      )}
    </Link>
  );
};

const useChatUnread = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const poll = () => api.get("/chat/me/unread").then((r) => setN(r.data.unread)).catch(() => {});
    poll();
    const id = setInterval(poll, 30000);
    window.addEventListener("vavlis:chat", poll);
    return () => { clearInterval(id); window.removeEventListener("vavlis:chat", poll); };
  }, []);
  return n;
};

const BottomNav = () => {
  const { t } = useI18n();
  const unread = useChatUnread();
  const cls = ({ isActive }) => `flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 truncate px-0.5 text-[10px] font-medium transition-colors sm:text-[11px] ${isActive ? "text-brand" : "text-slate-500 hover:text-navy"}`;
  return (
    <nav data-testid="bottom-nav" className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-lg">
      <div className="mx-auto flex max-w-xl">
        {TABS.map(({ to, end, icon: Icon, key }) => (
          <NavLink key={key} to={to} end={end} className={cls} data-testid={`bottom-nav-${key}`}>
            <span className="relative"><Icon className="h-5 w-5" />{key === "chat" && unread > 0 && <span data-testid="chat-unread-badge" className="absolute -right-2.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand px-1 text-[10px] font-bold text-navy">{unread}</span>}</span>
            <span>{t(`shop.nav.${key}`)}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

const Header = () => {
  const { t } = useI18n();
  const { logout, user } = useAuth();
  return (
    <header className="sticky top-0 z-30 bg-navy text-white">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-2 px-3 sm:px-4">
        <Link to="/app" data-testid="header-home-link"><Logo compact className="h-7" /></Link>
        {user.roles.slice(0, 1).map((r) => <RoleBadge key={r} role={r} className="ml-1" />)}
        <div className="ml-auto flex items-center gap-0.5 sm:gap-2">
          <LangSwitch dark />
          <NotificationBell dark />
          <CartButton />
          <button type="button" data-testid="logout-button" onClick={logout} aria-label={t("common.logout")} className="grid h-11 w-11 place-items-center rounded-full transition-colors hover:bg-white/10">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export const CustomerLayout = () => {
  const qc = useQueryClient();
  return (
    <CartProvider>
      <div className="min-h-screen bg-canvas">
        <ViewAsBar />
        <Header />
        <PullToRefresh onRefresh={() => qc.refetchQueries({ type: "active" })}>
          <main className="mx-auto max-w-6xl px-4 pb-28 pt-5 sm:pt-8"><Outlet /><OfferPopup /></main>
        </PullToRefresh>
        <BottomNav />
        {!sessionStorage.getItem("vavlis.viewas") && <PushPrompt />}
      </div>
    </CartProvider>
  );
};
